import {
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ItemExprUtils, ExtractExpr } from '../../utils/item-expr.utils';
import { RandomUtils } from '../../utils/random.utils';
import { IconAs } from '../iconic/iconic.component';

export interface RadioItemLike {
  label?: string;
  icon?: string;
  iconAs?: IconAs;
}

interface RadioGroupItemContext<T = any> {
  index: number;
  item: T;
}

@Directive({
  selector: 'ng-template[radioGroupItem]',
})
export class RadioGroupItemTemplate<T = any> {
  constructor(public templateRef: TemplateRef<RadioGroupItemContext<T>>) {}
}

@Component({
  selector: 'app-radio-group',
  templateUrl: 'radio-group.component.html',
  styleUrls: ['radio-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RadioGroupComponent,
    },
  ],
})
export class RadioGroupComponent implements ControlValueAccessor {
  @ContentChild(RadioGroupItemTemplate, { static: false })
  itemTplRef: RadioGroupItemTemplate;

  @Input()
  name: string = RandomUtils.uniqId();

  @Input()
  value: any;
  @Output()
  valueChange = new EventEmitter<any>();

  @Output()
  onItemSelected = new EventEmitter<any>();

  @Input()
  items: Array<RadioItemLike | any> = [];

  @Input()
  displayExpr: ExtractExpr = 'label';

  @Input()
  valueExpr: ExtractExpr = (item) => item;

  @Input()
  showIcon = true;

  @Input()
  showRadioButton = true;

  @Input()
  disabled: boolean;

  @Input()
  scrollable = true;

  private _touched = false;

  private onChange = (value) => {};

  private onTouched = () => {};

  writeValue(value: any): void {
    if (this.value !== value) {
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = !!isDisabled;
  }

  onRadioBtnClick(e, item: any, index: number, radioInputEl: HTMLInputElement) {
    radioInputEl.checked = true;

    const value = this.extractValue(item, index);
    if (this.value !== value) {
      this.value = value;
      this.valueChange.emit(this.value);
      this.onChange?.(this.value);

      this.onItemSelected.emit(item);
    }

    if (!this._touched) {
      this._touched = true;
      this.onTouched?.();
    }
  }

  isSelected(item: any, index: number, radioInputEl: HTMLInputElement) {
    const selected = this.extractValue(item, index) === this.value;

    if (selected && !radioInputEl.checked) {
      radioInputEl.checked = selected;
    }

    return selected;
  }

  extractDisplay(item: any, index: number) {
    return ItemExprUtils.extractValue(item, index, this.displayExpr);
  }

  extractValue(item: any, index: number) {
    return ItemExprUtils.extractValue(item, index, this.valueExpr);
  }
}
